import { Line, Text } from "@react-three/drei";
import type { ThreeInputLinesType } from "@tosize/shapes/src/types/three";
import { formatNumber } from "@tosize/ui";
import { Fragment, useMemo } from "react";
import { Path, Vector3 } from "three";
import { match } from "ts-pattern";
import { usePanelVisualisation } from "../../Context";
import { RENDER_QUALITY } from "../generic-shape/Shape";
import {
	ACTIVE_LINE_COLOR,
	ENDLINE_LENGTH,
	FONT_SIZE,
	LINE_COLOR,
	LINE_WIDTH,
	type LineType,
	OFFSET,
	TEXT_COLOR,
	addPathEndlines,
	getOuterDimensions,
	getPoints,
	isActiveLine,
} from "./utils";

export function InputLines({ currentField }: { currentField?: string }) {
	const { inputLines } = usePanelVisualisation();
	const fontSize = useMemo(() => {
		const lengths = inputLines?.map((inputLine) => inputLine.path.getLength()).filter(Number);
		const minLength = lengths ? Math.min(...lengths) : 0;

		return Math.max(FONT_SIZE * minLength, FONT_SIZE * 0.8);
	}, [inputLines]);

	return (
		<Fragment>
			{inputLines?.map((inputLine, index) => (
				<InputLine
					key={index}
					inputLine={inputLine}
					fontSize={fontSize}
					active={isActiveLine(inputLine.code, currentField)}
				/>
			))}
		</Fragment>
	);
}

interface InputLineProps {
	inputLine: ThreeInputLinesType;
	fontSize: number;
	active?: boolean;
}

function InputLine({ inputLine, fontSize, active }: InputLineProps) {
	const { lang, shape, sidePaths, isModified } = usePanelVisualisation();
	const points = useMemo(() => getPoints(inputLine.path), [inputLine]);

	const lineType: LineType = useMemo(
		() => (points.firstPoint.y === points.lastPoint.y ? "horizontal" : "vertical"),
		[points],
	);

	const length = useMemo(() => inputLine.path.getLength(), [inputLine]);

	const outerDimensions = useMemo(
		() => (sidePaths ? getOuterDimensions(sidePaths) : undefined),
		[sidePaths],
	);

	const groupPosition: Vector3 = useMemo(() => {
		return match(lineType)
			.returnType<Vector3>()
			.with("horizontal", () => {
				if (points.firstPoint.y === outerDimensions?.biggestY) {
					return new Vector3(
						Math.min(points.firstPoint.x, points.lastPoint.x),
						points.firstPoint.y + OFFSET * (inputLine?.doubleOffset ? 3 : 1),
						0,
					);
				}

				return new Vector3(
					Math.min(points.firstPoint.x, points.lastPoint.x),
					points.firstPoint.y + OFFSET * (inputLine?.doubleOffset ? -3 : -1),
					0,
				);
			})
			.with(
				"vertical",
				() =>
					new Vector3(
						Math.min(points.firstPoint.x, points.lastPoint.x) +
							OFFSET * (inputLine?.doubleOffset ? 3 : 1) * (points.firstPoint.x === 0 ? -1 : 1),
						points.firstPoint.y,
						0,
					),
			)
			.exhaustive();
	}, [points, inputLine, lineType, outerDimensions]);

	const textPosition = useMemo(() => {
		const length = inputLine.path.getLength();
		const halfHeight = (Number(outerDimensions?.biggestY) - Number(outerDimensions?.smallestY)) / 2;

		return match(lineType)
			.with("horizontal", () => {
				return new Vector3(length / 2, points.firstPoint.y >= halfHeight ? OFFSET : -OFFSET, 0);
			})
			.with("vertical", () => {
				return new Vector3(length / 2, points.firstPoint.x > 0 ? -OFFSET : OFFSET, 0);
			})
			.exhaustive();
	}, [
		inputLine.path,
		lineType,
		outerDimensions?.biggestY,
		outerDimensions?.smallestY,
		points.firstPoint.x,
		points.firstPoint.y,
	]);

	const groupRotation: number = useMemo(() => {
		if (lineType === "vertical") {
			return Math.PI / 2;
		}
		return 0;
	}, [lineType]);

	const textRotation = useMemo(() => {
		return match(lineType)
			.returnType<number>()
			.with("vertical", () => {
				return points.firstPoint.x > 0 ? Math.PI * 2 : 0;
			})
			.otherwise(() => 0);
	}, [lineType, points]);

	const linePoints = useMemo(() => {
		const path = new Path().moveTo(0, 0).lineTo(length, 0);

		return addPathEndlines(path, ENDLINE_LENGTH).getPoints(RENDER_QUALITY);
	}, [length]);

	const label = shape?.inputs?.find((input) => input.id === inputLine.code)?.label;
	const dimension = isModified
		? formatNumber({
				lang,
				value: inputLine.dimension / 10,
				unit: "centimeter",
				style: "unit",
			})
		: undefined;

	if (!inputLine.dimension) {
		return null;
	}

	const text = [label, dimension].filter(Boolean).join(" - ");

	return (
		<group position={groupPosition}>
			<group rotation={[0, 0, groupRotation]}>
				<Line
					points={linePoints}
					lineWidth={LINE_WIDTH}
					color={active ? ACTIVE_LINE_COLOR : LINE_COLOR}
				/>

				<Text
					fontSize={fontSize}
					anchorX="center"
					color={TEXT_COLOR}
					position={textPosition}
					rotation={[0, 0, textRotation]}
				>
					{text}
				</Text>
			</group>
		</group>
	);
}
